import Keycloak from 'keycloak-js';

class KeycloakService {
    constructor(config) {
        this.realmUrl = config.realmUrl;
        this.clientId = config.clientId;
        this.keycloak = null;
        this.initialized = false;
        this.initializing = false; // Flag to prevent concurrent initializations
    }

    async init() {
        // Prevent concurrent initializations
        if (this.initializing) {
            console.log('Keycloak initialization already in progress, waiting...');
            // Wait for current initialization to complete
            while (this.initializing) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            return this.keycloak;
        }
        
        if (this.initialized && this.keycloak) {
            // If already initialized and authenticated, return immediately
            if (this.keycloak.authenticated) {
                return this.keycloak;
            }
            // If initialized but not authenticated, check if we need to re-init
            // This can happen if callback wasn't processed correctly
            const hasKeycloakCallback = window.location.hash && (
                window.location.hash.includes('state=') || 
                window.location.hash.includes('code=') ||
                window.location.hash.includes('access_token=')
            );
            if (!hasKeycloakCallback) {
                return this.keycloak;
            }
            // If we have callback but not authenticated, reset and try again
            console.log('Keycloak callback present but not authenticated, resetting...');
            this.initialized = false;
            this.keycloak = null;
        }
        
        this.initializing = true;

        // Extract realm name from URL
        const realmMatch = this.realmUrl.match(/\/realms\/([^\/]+)/);
        const realm = realmMatch ? realmMatch[1] : 'master';
        const keycloakUrl = this.realmUrl.replace(/\/realms\/[^\/]+.*$/, '');
        
        // Build redirect URI - use origin + pathname without query/hash for consistency
        // For SPA apps, it's better to use root path to match Keycloak wildcard settings
        const redirectUri = window.location.origin + (window.location.pathname || '/');
        
        console.log('Keycloak redirectUri:', redirectUri);
        console.log('Make sure this URI is added to Keycloak Client Valid redirect URIs:', redirectUri);
        
        // Check if we're returning from Keycloak redirect (has hash fragment with state/code)
        const hasKeycloakCallback = window.location.hash && (
            window.location.hash.includes('state=') || 
            window.location.hash.includes('code=') ||
            window.location.hash.includes('access_token=')
        );
        
        console.log('Keycloak callback detected in URL:', hasKeycloakCallback);
        
        this.keycloak = new Keycloak({
            url: keycloakUrl,
            realm: realm,
            clientId: this.clientId
        });

        try {
            // If we have a callback in URL, try to process it first with check-sso
            // This allows Keycloak to process the callback without redirecting
            if (hasKeycloakCallback) {
                console.log('Processing Keycloak callback with check-sso...');
                try {
                    this.initialized = await this.keycloak.init({
                        onLoad: 'check-sso', // Use check-sso to process callback without redirect
                        pkceMethod: 'S256',
                        checkLoginIframe: false,
                        enableLogging: true,
                        redirectUri: redirectUri
                    });
                    console.log('Keycloak callback processed, initialized:', this.initialized, 'authenticated:', this.keycloak.authenticated);
                    
                    // If callback was processed successfully, clear hash
                    if (this.keycloak.authenticated) {
                        console.log('Clearing Keycloak callback from URL hash');
                        window.history.replaceState(null, '', window.location.pathname + window.location.search);
                    }
                } catch (callbackError) {
                    console.warn('Failed to process callback with check-sso, will try login-required:', callbackError);
                    // If check-sso failed, fall through to login-required
                    this.initialized = false;
                }
            }
            
            // If not authenticated after processing callback (or no callback), use login-required
            if (!this.initialized || !this.keycloak.authenticated) {
                console.log('Starting Keycloak init with login-required...');
                this.initialized = await this.keycloak.init({
                    onLoad: 'login-required', // Redirect to login if not authenticated
                    pkceMethod: 'S256',
                    checkLoginIframe: false,
                    enableLogging: true,
                    redirectUri: redirectUri
                });
                console.log('Keycloak init completed, initialized:', this.initialized, 'authenticated:', this.keycloak.authenticated);
            }
        } catch (error) {
            console.error('Keycloak initialization failed:', error);
            console.error('Keycloak init error details:', error.message, error.stack);
            this.initialized = false;
            this.initializing = false;
            throw error; // Re-throw to let caller handle it
        } finally {
            this.initializing = false;
        }

        return this.keycloak;
    }

    async login() {
        if (!this.keycloak) {
            await this.init();
        }
        // Explicitly set redirectUri for login
        const redirectUri = window.location.origin + (window.location.pathname || '/');
        console.log('Keycloak login redirectUri:', redirectUri);
        return this.keycloak.login({
            redirectUri: redirectUri
        });
    }

    async logout(options) {
        if (this.keycloak) {
            return this.keycloak.logout(options);
        }
    }

    getToken() {
        return this.keycloak?.token;
    }

    isAuthenticated() {
        return this.keycloak?.authenticated || false;
    }

    updateToken(minValidity = 5) {
        if (!this.keycloak) {
            return Promise.resolve(false);
        }
        return this.keycloak.updateToken(minValidity);
    }

    onTokenExpired(callback) {
        if (this.keycloak) {
            this.keycloak.onTokenExpired = callback;
        }
    }

    onAuthSuccess(callback) {
        if (this.keycloak) {
            this.keycloak.onAuthSuccess = callback;
        }
    }

    onAuthError(callback) {
        if (this.keycloak) {
            this.keycloak.onAuthError = callback;
        }
    }
}

let keycloakServiceInstance = null;

export function initKeycloak(config) {
    if (!keycloakServiceInstance && config && config.realmUrl && config.clientId) {
        keycloakServiceInstance = new KeycloakService(config);
    }
    return keycloakServiceInstance;
}

export function getKeycloakService() {
    return keycloakServiceInstance;
}


